/**
 * Shown when a real, authenticated identity carries no `orgId` claim.
 * SPEC-0003 §4.
 *
 * This case is expected rather than exceptional. Firebase Auth cannot deny
 * account creation (ADR-0012), so anyone with a Google account can sign in
 * successfully and still be entitled to nothing — that separation is the
 * design, and it deserves a real screen rather than a red error alert that
 * reads like the app broke.
 *
 * It offers no retry, because retrying cannot help: the claim is set by
 * `scripts/provision-user.ts`, and only an administrator can change it.
 */
import { Icon } from '../components/Icon';
import { Logo } from '../components/Logo';
import { ThemeToggle } from '../components/ThemeToggle';
import { useAuth } from '../lib/auth';

export function NotAuthorized() {
	const { user, signOutNow } = useAuth();

	return (
		<main className="bg-base-100 flex min-h-screen items-center justify-center p-6">
			<div className="fixed top-4 right-4 z-10">
				<ThemeToggle />
			</div>

			<div className="w-full max-w-md">
				<div className="mb-6 flex flex-col items-center gap-1">
					<Logo className="gap-2.5 text-4xl" iconClassName="size-10" />
				</div>

				<div className="card bg-base-200 border-base-300 border shadow-xs">
					<div className="card-body items-center gap-4 text-center">
						<Icon name="warning" className="size-8 opacity-70" />
						<div>
							<h2 className="card-title justify-center text-base">
								This account isn't enabled for Picklii
							</h2>
							<p className="text-base-content/70 mt-2 text-sm">
								You're signed in as{' '}
								<span className="font-medium">{user?.email}</span>, but that address
								isn't on the access list, so there's nothing here for it to show you.
							</p>
						</div>
						<p className="text-base-content/70 text-sm">
							Ask an administrator to add you, then sign in again.
						</p>
						<button className="btn btn-sm btn-outline" onClick={() => void signOutNow()}>
							<Icon name="logout" />
							Sign out
						</button>
					</div>
				</div>
			</div>
		</main>
	);
}
